// L10 (boot-health #3986): coordination primitives for warm-resume nav restore.
//
// After warm resume drops the splash, restoreNavigationStack rebuilds the page stack by pushing
// pages one at a time. Two problems this module's decisions address:
//   - L10 stack-clobber race: Phase B's post-login unlock re-route (setRootAsync(Home)) can fire
//     WHILE the restore is still pushing pages, clobbering the half-rebuilt stack (blank/wrong
//     page for the paid-after-expiry cohort). A re-route requested during a healthy restore must
//     be DEFERRED until the restore finishes.
//   - Cancellability (prerequisite for the Step 12 / L8 navRestoring watchdog): the restore's
//     page-load loop must be able to STOP between pushes once the restore is abandoned.
//
// Pure so it runs under node for unit tests (same pattern as splash-watchdog.ts); nav-controller
// holds the state + side effects and delegates the decisions here.

/**
 * Should the nav-restore page-load loop push the next page? Once the restore has been abandoned
 * — a merely-slow restore asked to stop, or Step 12's watchdog declaring a wedged one dead — the
 * loop stops iterating. (It can only stop BETWEEN pushes; an already-in-flight `await nav.push`
 * is not interruptible — that is exactly why Step 12's watchdog also force-clears the guard.)
 */
export function shouldContinueNavRestore(state: { abandoned: boolean }): boolean {
  return !state.abandoned;
}

/**
 * Should a re-route be deferred because a nav restore is in flight? During a healthy restore we
 * defer (queue) it so it can't clobber the stack mid-rebuild; it fires when the restore finishes.
 * (The Step 12 watchdog does not use this path — it force-clears a dead restore first, then
 * re-roots directly, so a recovery re-route never queues behind a restore that will never finish.)
 */
export function shouldDeferReroute(state: { isRestoring: boolean }): boolean {
  return state.isRestoring;
}

/**
 * L9 (#3986): after waitNavReadyAsync finishes polling for the nav component, decide whether
 * the outcome is a reportable condition. If nav never became ready within the budget, boot proceeds with an
 * undefined nav (broken navigation) — which used to be a bare console.error, invisible in prod.
 * Report it so a future debugger sees it.
 */
export function shouldReportNavNeverReady(navBecameReady: boolean): boolean {
  return !navBecameReady;
}

/**
 * L8 (#3986): how long after `navRestoring` becomes true the watchdog waits before declaring the
 * restore wedged. The navRestoring spinner window runs AFTER appLoading=false, so the splash
 * watchdog no longer covers it — a hung page-load during restoreNavigationStack is a forever
 * spinner with zero telemetry without this.
 */
export const NAV_RESTORE_WATCHDOG_MS = 20_000;

/**
 * L8 (#3986): when the navRestoring watchdog timer fires, decide whether to recover. Fire only if
 * the restore has NOT completed (navRestoring is still active). This must be keyed on restore
 * completion, NOT on bootStage: bootStage is already Ready before navRestoring flips true, so a
 * "navRestoring && bootStage !== Ready" predicate could never fire (the original bug).
 *
 * A pending app reload SUPPRESSES the watchdog: a restore superseded by an intentional reload is
 * moot, not wedged, so reporting it as NavRestoreStuck is a false positive. Sign-out is the case
 * that forced this rule, and it is easy to reintroduce — the full reasoning lives in
 * design-docs/boot-sequence.md ("L8: a pending reload suppresses the watchdog").
 *
 * A hidden document ALSO suppresses the watchdog (#4232): while the app is backgrounded the OS
 * pauses the page loads the restore is waiting on, so a restore that made no progress is paused,
 * not wedged. Firing there produced false NavRestoreStuck reports the moment the app resumed
 * (a 17-minute background gap counted toward the 20 s window). The caller pauses the timer on
 * visibilitychange→hidden and re-arms with the remaining foreground budget on visible; this
 * predicate is the belt-and-braces backstop for a deadline that still runs while hidden.
 */
export function shouldFireNavRestoreWatchdog(
  state: { navRestoringStillActive: boolean, appReloadPending: boolean, documentHidden: boolean },
): boolean {
  // if the app is intentionally reloading, the restore is moot rather than wedged
  if (state.appReloadPending) {
    return false;
  }
  // if the app is backgrounded, the restore is paused by the OS rather than wedged (#4232)
  if (state.documentHidden) {
    return false;
  }
  return state.navRestoringStillActive;
}

/**
 * #4232: how much of the watchdog's foreground budget remains after a stretch of visible time.
 * The watchdog must measure FOREGROUND time only — backgrounded time proves nothing about the
 * restore being wedged — so on visibilitychange→hidden the caller stops the timer and stores
 * this remainder, and on visible it re-arms with it. Clamped at 0: a deadline that was due
 * exactly when the app was hidden has no budget left, and the caller evaluates immediately.
 */
export function remainingNavRestoreWatchdogMs(
  state: { remainingMs: number, elapsedVisibleMs: number },
): number {
  return Math.max(0, state.remainingMs - state.elapsedVisibleMs);
}

/**
 * #4232: what the app coming back to the foreground does to the watchdog.
 */
export enum NavRestoreWatchdogResume {
  // nothing to do: no watchdog window is open, the restore is done, or a countdown is running
  None = 'none',
  // start the countdown again with the banked foreground budget
  Restart = 'restart',
  // the budget was already used up — decide right now whether the restore is wedged
  EvaluateNow = 'evaluate-now',
}

/**
 * #4232: decide what a visibilitychange→visible does to the watchdog.
 *
 * `windowOpen` is whether the watchdog is armed for a restore at all. It is NOT the same as
 * `navRestoring`: the cold boot turns the spinner on before its community-page and nav-ready awaits,
 * but arms the watchdog only when the restore itself starts (df-f938864e16d1). In that gap the budget
 * reads 0 because nothing was armed yet, not because it ran out — so an open window is required
 * before a 0 budget may be read as "used up".
 */
export function navRestoreWatchdogResumeAction(
  state: { windowOpen: boolean, navRestoring: boolean, timerRunning: boolean, remainingMs: number },
): NavRestoreWatchdogResume {
  // if nothing is armed, or the restore already finished, there is no countdown to resume
  if (!state.windowOpen || !state.navRestoring) {
    return NavRestoreWatchdogResume.None;
  }
  // if a countdown is already running (a visible event without a preceding hidden), don't double-arm
  if (state.timerRunning) {
    return NavRestoreWatchdogResume.None;
  }
  if (state.remainingMs > 0) {
    return NavRestoreWatchdogResume.Restart;
  }
  return NavRestoreWatchdogResume.EvaluateNow;
}

/**
 * How a boot's wait on the nav restore ended (df-d80a99934799).
 */
export enum NavRestoreOutcome {
  // the restore finished by itself
  Completed = 'completed',
  // the L8 watchdog declared the restore wedged and rescued the screen first
  Recovered = 'recovered',
}

/**
 * Wait for the nav restore, or for the watchdog to rescue it — whichever comes first.
 *
 * The cold boot awaits the restore and then finishes the boot: the `booting_time` event, the
 * "ready" boot stage, the paid-user background tasks. A wedged restore never settles, and the
 * watchdog's rescue does nothing to that promise, so without this race the rest of the boot never
 * ran for the life of the page. `recovery` is resolved by the watchdog's rescue.
 *
 * A restore that REJECTS before any rescue rethrows, exactly as a plain await did. A restore that
 * rejects AFTER a rescue has nobody awaiting it any more, so `onLateFailure` reports it instead of
 * letting it become an unhandled rejection. A late SUCCESS needs nothing.
 */
export async function awaitNavRestoreOrRecovery(
  restore: Promise<unknown>,
  recovery: Promise<void>,
  onLateFailure: (err: unknown) => void,
): Promise<NavRestoreOutcome> {
  let isRecovered = false;
  // attached up front, so a rejection after the race is decided is still handled
  void restore.catch(err => {
    if (isRecovered) {
      onLateFailure(err);
    }
  });
  return Promise.race([
    restore.then(() => NavRestoreOutcome.Completed),
    recovery.then(() => {
      // set here, the moment the rescue wins, and not after the race's own await resumes: a restore
      // rejecting in the microtasks between the two would otherwise be neither rethrown nor reported
      isRecovered = true;
      return NavRestoreOutcome.Recovered;
    }),
  ]);
}
